<template>
  <component
    :is="content.coming_soon ? 'div' : 'router-link'"
    :class="['card', { 'card--coming-soon': content.coming_soon }]"
    v-bind="content.coming_soon ? {} : { to: `/${$i18n.locale}/${content.link}` }"
    :data-project="content.link"
    @click="onCardClick"
  >
    <!-- Card box: image only -->
    <div class="card__box">
      <div class="card__media">
        <img
          :src="imgSrc(`assets/work-gallery/thumbnails/${content.image}`)"
          :alt="`${content.title} project image`"
        />
        <div v-if="content.coming_soon" class="card__overlay">
          <span class="card__overlay-label">Coming soon</span>
        </div>
      </div>
    </div>

    <!-- Text content: sits below the box -->
    <div class="card__body">
      <!-- Logo -->
      <!-- <img v-if="content && content.logo" :src="imgSrc(`assets/work-gallery/logos/${content.logo}`)" :alt="`${content.title}`" class="card__logo" /> -->

      <!-- Title + Description -->
      <div class="card__text">
        <h3 class="card__title">{{ content.title }}</h3>
        <p class="card__desc">{{ content.description }}</p>
        <div v-if="content.tags && content.tags.length" class="card__tags">
          <span v-for="tag in content.tags" :key="tag" class="badge badge--outline">{{ tag }}</span>
        </div>
        <p v-if="content && content.disclaimer" class="card__disclaimer">{{ content.disclaimer }}</p>
      </div>
    </div>
  </component>
</template>

<script>
import { trackProjectClick } from '../analytics/tracker';

export default {
  name: "CardProject",
  props: {
    content: Object,
  },
  computed: {
    imgSrc() {
      return (urlFile) => {
        try {
          return require(`../${urlFile}`);
        } catch (e) {
          return require("../assets/img-placeholder.jpg");
        }
      }
    },
  },
  methods: {
    onCardClick() {
      if (!this.content.coming_soon) {
        trackProjectClick(this.content.link);
      }
    },
  },
};
</script>

<style scoped>
.card {
  display: block;
  width: 100%;
  text-decoration: none;
}

.card.card--coming-soon {
  cursor: default;
}

.card.card--coming-soon:hover .card__box {
  box-shadow: none;
  border-color: var(--border-subtle);
  transform: none;
}

.card__box {
  background: var(--surface-raised);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xl);
  overflow: hidden;
  transition:
    box-shadow var(--duration-normal) var(--ease-default),
    border-color var(--duration-normal) var(--ease-default),
    transform var(--duration-normal) var(--ease-default);
}

.card:hover .card__box {
  box-shadow: var(--shadow-lg);
  border-color: var(--border-default);
  transform: translateY(-3px);
}

.card__media {
  position: relative;
  width: 100%;
  aspect-ratio: 1/1;
  overflow: hidden;
  background: var(--surface-elevated);
  padding: var(--space-8);
  display: flex;
  align-items: center;
  justify-content: center;
}

.card__media img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  transition: transform var(--duration-slow) var(--ease-default);
}

.card:hover .card__media img {
  transform: scale(1.03);
}

.card__body {
  padding: var(--space-4) 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.card__logo {
  height: 1.5rem;
  width: auto;
  flex-shrink: 0;
  margin-top: var(--space-1);
}

.card__text {
  display: flex;
  flex-direction: column;
}

.card__title {
  font-family: var(--font-display);
  font-size: var(--text-xl);
  font-weight: var(--weight-medium);
  line-height: 1.3;
  color: var(--text-primary);
  margin-bottom: var(--space-2);
}

.card__desc {
  font-family: system-ui;
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
  color: var(--text-secondary);
  margin-bottom: var(--space-3);
}

@media (min-width: 768px) {
  .card__title {
    font-size: var(--text-2xl);
  }
  .card__desc{
    font-size: var(--text-base);
  }
}

.card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-bottom: var(--space-4);
}

.badge {
  display: inline-flex;
  align-items: center;
  font-family: var(--font-sans);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  line-height: var(--leading-none);
  padding: var(--space-1) var(--space-3);
  border-radius: 4px;
  border: 1px solid transparent;
  white-space: nowrap;
}

.badge--outline {
  background: transparent;
  color: var(--text-tertiary);
  border-color: var(--border-default);
  letter-spacing: var(--tracking-wider);
}

.card__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity var(--duration-normal) var(--ease-default);
  border-radius: var(--radius-xl);
}

.card:hover .card__overlay {
  opacity: 1;
}

.card__overlay-label {
  font-family: var(--font-sans);
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-wide);
  color: #ffffff;
}

.card__disclaimer {
  font-family: var(--font-sans);
  font-size: var(--text-xs);
  color: var(--text-tertiary);
  line-height: var(--leading-relaxed);
}

</style>
